import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { getIconPath } from '../../Functions/GetIconPath';
import Button from '../Button/Button';

import styles from './RightMenu.module.scss';

export interface RightMenuPropsI {
    children: React.ReactNode;
    rightMenuOpen?: boolean;
    onClick: () => void;
    onExport?: () => void;
}


const RightMenu = ({ children, rightMenuOpen, onClick, onExport }: RightMenuPropsI): React.ReactElement => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(rightMenuOpen);
    
    useEffect(() => {
        setIsOpen(rightMenuOpen);
    }, [rightMenuOpen]);

    const handleClick = () => {
        setIsOpen(!isOpen);
        onClick();
    }

    const handleExport = () => {
        onExport?.();
    }

    const menuClasses = classNames({
        [styles.rightMenu__open]: isOpen,
        [styles.rightMenu__closed]: !isOpen
    });

    /*
    <div className={classNames((isOpen ? styles.rightMenu__heading__open : styles.rightMenu__heading__closed), styles.rightMenu__heading)}>
        <h2>{t('rightMenu.heading')}</h2>
    </div>
    */

    return (
        <>
            <div id="tutorial-right-menu" className={classNames(styles.rightMenu, menuClasses)}>
                <div className={classNames((isOpen ? styles.rightMenu__top__open : styles.rightMenu__top__closed), styles.rightMenu__top)}>
                    <div className={styles.rightMenu__top__open__button} style={{ display: isOpen ? 'block' : 'none' }}>
                        <Button size='md' variant='fill' onClick={() => handleExport()} >{t('rightMenu.export')}</Button>
                    </div>
                    <img onClick={handleClick} style={{ width: '3rem' }} src={getIconPath('menu-icon')} alt="Menu Icon" />
                </div>
                
                <div className={classNames((isOpen ? styles.rightMenu__content__open : styles.rightMenu__content__closed), styles.rightMenu__content)}>
                    {children}
                </div>
            </div>
        </>
    );
}

export interface RightMenuAppliancesPropsI {
    children: React.ReactNode;
    rightMenuOpen: boolean;
    collapsed: boolean;
}

const RightMenuAppliances = ({ children, rightMenuOpen, collapsed }: RightMenuAppliancesPropsI) => {
    const { t } = useTranslation();
    const menuClasses = classNames({
        [styles.rightMenu__content__appliances__open]: rightMenuOpen,
        [styles.rightMenu__content__appliances__closed]: !rightMenuOpen,
        [styles.rightMenu__content__appliances__collapsed]: collapsed,
        [styles.rightMenu__content__appliances__uncollapsed]: !collapsed
    })
    return (
        <div className={classNames(menuClasses, styles.rightMenu__content__appliances__list)}>
            <div className={styles.rightMenu__content__appliances__collapsedMessage}>
                <p>{React.Children.count(children)} {t('rightMenu.collapsed.appliances')}</p>
            </div>
            <div className={styles.rightMenu__content__appliances__items}>
                {children}
            </div>
        </div>
    );
};

export interface RightMenuDevicesPropsI {
    children: React.ReactNode;
    rightMenuOpen: boolean;
    collapsed: boolean;
    addButton?: React.ReactNode;
}

const RightMenuDevices = ({ children, rightMenuOpen, collapsed, addButton}: RightMenuDevicesPropsI) => {
    const { t } = useTranslation();
    const menuClasses = classNames({
        [styles.rightMenu__content__devices__open]: rightMenuOpen,
        [styles.rightMenu__content__devices__closed]: !rightMenuOpen,
        [styles.rightMenu__content__devices__collapsed]: collapsed,
        [styles.rightMenu__content__devices__uncollapsed]: !collapsed
    })
    return (
        <div className={classNames(menuClasses, styles.rightMenu__content__devices__list)}>
            <div className={styles.rightMenu__content__devices__collapsedMessage}>
                <p>{React.Children.count(children)} {t('rightMenu.collapsed.devices')}</p>
            </div>
            <div className={styles.rightMenu__content__devices__items}>
                {children}
                {addButton}
            </div>
        </div>
    );
}

export interface RightMenuActuatorsPropsI {
    children: React.ReactNode;
    rightMenuOpen: boolean;
    collapsed: boolean;
}

const RightMenuActuators = ({ children, rightMenuOpen, collapsed }: RightMenuActuatorsPropsI) => {
    const { t } = useTranslation();
    const menuClasses = classNames({
        [styles.rightMenu__content__actuators__open]: rightMenuOpen,
        [styles.rightMenu__content__actuators__closed]: !rightMenuOpen,
        [styles.rightMenu__content__actuators__collapsed]: collapsed,
        [styles.rightMenu__content__actuators__uncollapsed]: !collapsed
    })
    return (
        <div className={classNames(menuClasses, styles.rightMenu__content__actuators__list)}>
            <div className={styles.rightMenu__content__actuators__collapsedMessage}>
                <p>{React.Children.count(children)} {t('rightMenu.collapsed.actuators')}</p>
            </div>
            <div className={styles.rightMenu__content__actuators__items}>
                {children}
            </div>
        </div>
    );
};

RightMenu.Appliances = RightMenuAppliances;
RightMenu.Devices = RightMenuDevices;
RightMenu.Actuators = RightMenuActuators;

export interface RightMenuDetectorsPropsI {
    children: React.ReactNode;
    rightMenuOpen: boolean;
    collapsed: boolean;
}

const RightMenuDetectors = ({ children, rightMenuOpen, collapsed }: RightMenuDetectorsPropsI) => {
    const { t } = useTranslation();
    const menuClasses = classNames({
        [styles.rightMenu__content__actuators__open]: rightMenuOpen,
        [styles.rightMenu__content__actuators__closed]: !rightMenuOpen,
        [styles.rightMenu__content__actuators__collapsed]: collapsed,
        [styles.rightMenu__content__actuators__uncollapsed]: !collapsed
    })
    return (
        <div className={classNames(menuClasses, styles.rightMenu__content__actuators__list)}>
            <div className={styles.rightMenu__content__actuators__collapsedMessage}>
                <p>{React.Children.count(children)} {t('rightMenu.collapsed.detectors')}</p>
            </div>
            <div className={styles.rightMenu__content__actuators__items}>
                {children}
            </div>
        </div>
    );
};

RightMenu.Detectors = RightMenuDetectors;

export default RightMenu;